/**
 * A composed answer as it may be KEPT on this device (#296): its references and how it was made — never its words.
 * The server keeps a thread's snapshot the same way (`knowledge.to_annotation.redact_composed`); a reopened chat
 * shows a placeholder and the words are re-read through the viewer's keys when asked again.
 */
export function isComposedTurn(t: any): boolean {
  return !!t && typeof t === 'object' && Array.isArray(t.clauses) && 'made' in t;
}

export function redactComposedTurn<T extends Record<string, any>>(t: T): T {
  // a group turn (#307) carries its member's composed reply NESTED: redacted the same way, by reference
  if (t && typeof t === 'object' && !isComposedTurn(t) && isComposedTurn((t as any).reply)) {
    return { ...t, reply: redactComposedTurn((t as any).reply) } as T;
  }
  if (!isComposedTurn(t) || (t as any).redacted) return t;
  const { text: _text, template: _template, clauses, ...rest } = t as any;
  const refs = (clauses as any[]).map((c) => {
    const ref: any = { slot: c?.slot, kind: c?.kind, tier: c?.tier };
    if (c?.kind === 'number') { ref.claim = { id: c.claim?.id, session_id: c.claim?.session_id }; ref.grounding = c.claim?.grounding; }
    else if (c?.kind === 'quote') { const { quote: _q, ...span } = c.span || {}; ref.span = span; }
    return ref;
  });
  return { ...rest, clauses: refs, redacted: true } as T;
}

/** Turns as they may be parked: every composed answer by reference. */
export function redactTurns(turns: any[]): any[] {
  return (turns || []).map((t) => redactComposedTurn(t));
}


/** A claim projected from the brain, wherever it sits: kind `context`, or an id in the kc: namespace. */
function isClaimRecord(v: any): boolean {
  return !!v && typeof v === 'object' && !Array.isArray(v) && (v.kind === 'context' || v.annotation_kind === 'context' || String(v.id || '').startsWith('kc:'));
}

function refOf(v: any): string {
  const span = v?.provenance?.span || v?.sources?.[0]?.meta?.span;
  return span ? `a claim from the brain · ${span.document_id}#${span.start}-${span.end}` : `a claim from the brain · ${v?.id}`;
}

const KEEP = new Set(['claim_id', 'document_id', 'content_hash', 'span', 'connection_id', 'tier', 'machine_origin', 'source_type', 'asserted_by', 'asserted_by_name', 'asserted_at', 'method']);

/**
 * Anything kept on this device that may hold a claim's words (#296) — a thread's turns, a pin — keeps the claim's
 * identity (id, span, tier) and loses the words: the server's `knowledge.to_annotation.redact_nested`, mirrored.
 */
export function redactClaimWords<T>(value: T): T {
  const renamed = new Map<string, string>();
  const collect = (v: any) => {
    if (Array.isArray(v)) v.forEach(collect);
    else if (v && typeof v === 'object') {
      if (isClaimRecord(v)) for (const key of ['title', 'label']) if (typeof v[key] === 'string' && v[key]) renamed.set(v[key], refOf(v));
      Object.values(v).forEach(collect);
    }
  };
  collect(value);
  const walk = (v: any): any => {
    if (typeof v === 'string') {
      let out = v;
      renamed.forEach((to, from) => { if (out.includes(from)) out = out.split(from).join(to); });
      // a string cut to a length (a preview) may end inside a title: the title's prefix (12+ chars) at the end goes too
      renamed.forEach((_to, from) => { for (let k = from.length - 1; k >= 12; k -= 1) { if (out.endsWith(from.slice(0, k))) { out = `${out.slice(0, -k).trimEnd()}…`; break; } } });
      return out;
    }
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === 'object') {
      let cur: any = v;
      if (isComposedTurn(cur)) cur = redactComposedTurn(cur);
      else if (isClaimRecord(cur)) {
        const prov: any = cur.provenance || {};
        cur = { ...cur, title: refOf(cur), body: null, provenance: Object.fromEntries(Object.entries(prov).filter(([k]) => KEEP.has(k))),
          ...('label' in cur ? { label: refOf(cur) } : {}),
          ...(Array.isArray(cur.sources) ? { sources: cur.sources.map((s: any) => (s && typeof s === 'object' ? { ...s, detail: refOf(cur) } : s)) } : {}) };
      } else if (cur.span && typeof cur.span === 'object' && 'quote' in cur.span) {
        const { quote: _q, ...span } = cur.span;
        cur = { ...cur, span, ...('value' in cur ? { value: null } : {}) };
      }
      return Object.fromEntries(Object.entries(cur).map(([k, x]) => [k, walk(x)]));
    }
    return v;
  };
  return walk(value);
}
